import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Wand2, Image as ImageIcon, Download, Trash2, Search, Loader2,
  AlertTriangle, Sparkles, ImagePlus, Plug, ExternalLink, LayoutTemplate,
  Upload, FileImage, Palette, X, Mic2, Disc3, CalendarHeart, Star, Radio,
  Megaphone, Trophy, Cake, Cloud, ShieldAlert, Music4, Newspaper, PartyPopper,
  Stamp, Film, Send,
} from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/PageHeader";
import WatermarkUnlockPrompt from "@/components/WatermarkUnlockPrompt";
import CreativeStudioBlogPublishModal from "@/components/CreativeStudioBlogPublishModal";
import { api } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";

const ASPECTS = [
  { key: "square",    label: "Square",    dim: "1:1 · 1024",  aspect: "1 / 1"    },
  { key: "portrait",  label: "Portrait",  dim: "9:16 · 1080", aspect: "9 / 16"   },
  { key: "landscape", label: "Landscape", dim: "16:9 · 1920", aspect: "16 / 9"   },
  { key: "hd",        label: "HD",        dim: "16:9 · HD",   aspect: "16 / 9"   },
];

const PROMPT_IDEAS = [
  "Radio station logo, retro neon, vinyl record motif, glowing 'BroadcastFlow' wordmark",
  "Promotional poster for an indie pop artist named ARIA — moody purple/orange gradient, halftone texture",
  "Top-10 countdown thumbnail with chart bars and a microphone, dark background, electric cyan accents",
  "Holiday station promo: snowy skyline, glowing FM dial, headline 'Holiday Hits Non-Stop'",
];

// Phase 1.5 — broadcast-grade template builder. Each template composes a
// polished, model-friendly prompt by filling its {placeholders} with the
// user's inputs. `fields` define the labelled inputs shown in the picker.
const TEMPLATES = [
  {
    id: "artist_spotlight",
    label: "Artist Spotlight",
    icon: Mic2,
    description: "Hero poster celebrating an artist or track.",
    aspect: "portrait",
    fields: [
      { key: "artist", label: "Artist", placeholder: "e.g. ARIA", required: true },
      { key: "mood",   label: "Mood / genre", placeholder: "e.g. moody indie pop" },
    ],
    build: (v) => `Portrait promotional poster for the artist "${v.artist}". ${v.mood ? v.mood + " aesthetic. " : ""}Editorial halftone texture, bold colour grade, broadcast-grade typography for an artist-spotlight feature. High-detail, magazine-cover quality.`,
  },
  {
    id: "now_playing",
    label: "Now Playing Graphic",
    icon: Disc3,
    description: "Branded card for live now-playing overlay.",
    aspect: "landscape",
    fields: [
      { key: "song", label: "Song & artist", placeholder: "e.g. Get Lucky — Daft Punk", required: true },
    ],
    build: (v) => `Landscape "Now Playing" overlay card for radio: prominent vinyl record motif on the left, large clean type on the right reading "${v.song}", electric cyan + amber accent lighting, deep night-black background. Cinematic, broadcast quality.`,
  },
  {
    id: "concert_announce",
    label: "Concert Announce",
    icon: PartyPopper,
    description: "Eye-catching live-event announcement.",
    aspect: "square",
    fields: [
      { key: "event", label: "Event name", placeholder: "e.g. NEON DRIVE LIVE", required: true },
      { key: "venue", label: "Venue / date",  placeholder: "e.g. Brixton Academy · Mar 14" },
    ],
    build: (v) => `Square concert announcement poster for "${v.event}". ${v.venue ? "Venue line: " + v.venue + ". " : ""}Retro synthwave grid, pink/cyan neon glow, dramatic stage spotlights, bold headline typography, ticket-CTA aesthetic. High-detail cinematic poster.`,
  },
  {
    id: "podcast_promo",
    label: "Podcast Promo",
    icon: Radio,
    description: "Square podcast cover with episode title.",
    aspect: "square",
    fields: [
      { key: "podcast", label: "Show title", placeholder: "e.g. The Off Air Show", required: true },
      { key: "episode", label: "Episode line", placeholder: "e.g. 'Episode 42 — The Big Sound Shift'" },
    ],
    build: (v) => `Square podcast cover art for "${v.podcast}". ${v.episode ? v.episode + " " : ""}Bold geometric microphone illustration, deep amber + ink palette, modern editorial typography, broadcast-pro polish. Crisp 1:1 composition.`,
  },
  {
    id: "top10",
    label: "Top 10 Countdown",
    icon: Trophy,
    description: "Weekly chart show thumbnail.",
    aspect: "landscape",
    fields: [
      { key: "show", label: "Show name", placeholder: "e.g. Top 10 @ 10", required: true },
    ],
    build: (v) => `Landscape thumbnail for "${v.show}" chart show: rising chart bars, large golden "10" element, microphone silhouette, dramatic stage lighting, premium broadcast typography. High contrast, YouTube-ready clickability.`,
  },
  {
    id: "holiday_promo",
    label: "Holiday Promo",
    icon: CalendarHeart,
    description: "Seasonal station campaign.",
    aspect: "landscape",
    fields: [
      { key: "headline", label: "Headline", placeholder: "e.g. Holiday Hits Non-Stop", required: true },
    ],
    build: (v) => `Landscape holiday-season radio promo: snowy city skyline at dusk, glowing FM dial in the foreground, warm amber + festive red palette, bold clean typography for the headline "${v.headline}". Cinematic, broadcast-grade.`,
  },
  {
    id: "sponsor_ad",
    label: "Sponsor Ad",
    icon: Megaphone,
    description: "Display ad with logo + CTA space.",
    aspect: "landscape",
    fields: [
      { key: "business", label: "Business", placeholder: "e.g. Otis Coffee Roasters", required: true },
      { key: "cta",      label: "Call-to-action", placeholder: "e.g. Visit otiscoffee.co" },
    ],
    build: (v) => `Landscape sponsor advertisement layout for "${v.business}". ${v.cta ? "Call-to-action area: '" + v.cta + "'. " : ""}Clean editorial composition with negative space on the right for headline/CTA, premium product hero on the left, broadcast-grade colour palette. Web-banner aspect ratio.`,
  },
  {
    id: "station_id",
    label: "Station ID",
    icon: Radio,
    description: "Iconic visual signature card.",
    aspect: "square",
    fields: [
      { key: "station", label: "Station name", placeholder: "e.g. WAVE 95.7", required: true },
    ],
    build: (v) => `Square iconic station-ID visual for "${v.station}". Bold modernist typography, dynamic radio-wave motif, amber glow on deep ink background, monogram quality. Logo-tier composition.`,
  },
  {
    id: "contest_promo",
    label: "Contest Promo",
    icon: Star,
    description: "Listener competition graphic.",
    aspect: "square",
    fields: [
      { key: "prize", label: "Prize / contest", placeholder: "e.g. Win VIP tickets", required: true },
    ],
    build: (v) => `Square contest-promotion graphic: spotlights and confetti bursts, golden ticket motif, big bold "WIN" headline area, prize line reading "${v.prize}". Vibrant high-energy palette, broadcast-grade poster.`,
  },
  {
    id: "weather_card",
    label: "Weather Card",
    icon: Cloud,
    description: "Daily weather overlay graphic.",
    aspect: "landscape",
    fields: [
      { key: "city", label: "City", placeholder: "e.g. Manchester", required: true },
    ],
    build: (v) => `Landscape weather overlay card for "${v.city}": clean modern UI, sun/cloud iconography in the centre, premium broadcast typography around the edges, deep ink-blue + amber palette. Cinematic, magazine-ready quality.`,
  },
  {
    id: "emergency_alert",
    label: "Emergency Alert",
    icon: ShieldAlert,
    description: "High-visibility alert overlay.",
    aspect: "landscape",
    fields: [
      { key: "headline", label: "Alert headline", placeholder: "e.g. Severe Weather Warning", required: true },
    ],
    build: (v) => `Landscape emergency-alert broadcast overlay: high-contrast amber/red diagonal stripes, official-style shield icon, large clean typography for the headline "${v.headline}". Authoritative, instantly readable.`,
  },
  {
    id: "new_music_friday",
    label: "New Music Friday",
    icon: Music4,
    description: "Weekly new-release showcase.",
    aspect: "square",
    fields: [],
    build: () => `Square "New Music Friday" weekly showcase graphic: stacked vinyl records bursting with light, sound-wave halo, bold modern typography reading "NEW MUSIC FRIDAY", electric purple + amber palette. Cinematic, social-media optimized.`,
  },
];

const STYLE_PRESETS = [
  { id: "cartoon",     label: "Cartoon",     hint: "Bold outlines, flat colour" },
  { id: "illustration",label: "Illustration",hint: "Editorial print style" },
  { id: "watercolor",  label: "Watercolor",  hint: "Soft paint washes" },
  { id: "comic_book",  label: "Comic Book",  hint: "Halftone, inked lines" },
  { id: "render_3d",   label: "3D Render",   hint: "Octane-quality 3D" },
  { id: "neon",        label: "Neon",        hint: "Synthwave glow" },
  { id: "vintage",     label: "Vintage",     hint: "Faded film grain" },
];

const EDIT_PRESETS = [
  { id: "bg_remove",   label: "Remove Background", hint: "Transparent PNG" },
  { id: "bg_replace",  label: "Replace Background",hint: "Describe a new scene" },
  { id: "object_remove",label: "Remove Object",    hint: "Describe what to erase" },
  { id: "object_add",  label: "Add Object",        hint: "Describe what to add" },
  { id: "color_boost", label: "Color Boost",       hint: "Vibrant, on-brand colour" },
  { id: "lighting",    label: "Enhance Lighting",  hint: "Cinematic relight" },
  { id: "expand",      label: "Expand Canvas",     hint: "Outpaint borders" },
  { id: "add_text",    label: "Add Headline",      hint: "Overlay broadcast text" },
];

function authedImageUrl(path) {
  // Build absolute URL using REACT_APP_BACKEND_URL; auth cookie covers it.
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}${path}`;
}

export default function CreativeStudio() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [tab, setTab] = useState("generate"); // "generate" | "edit"
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("square");
  const [title, setTitle] = useState("");
  const [generating, setGenerating] = useState(false);
  const [lastImage, setLastImage] = useState(null);
  // Update #131 — publish-to-WordPress modal state
  const [publishImage, setPublishImage] = useState(null); // {image_id, title, prompt} or null

  const sendToKaraoke = useCallback((img) => {
    if (!img?.image_id) return;
    const bgUrl = `/api/creative-studio/image/${img.image_id}`;
    toast.message("Opening Beds & FX with your custom karaoke background…");
    navigate(`/dashboard/imaging?tab=assets&karaoke_bg=${encodeURIComponent(bgUrl)}`);
  }, [navigate]);

  // Phase 1.5 — template builder modal
  const [pickingTemplate, setPickingTemplate] = useState(null); // template obj or null
  const [tplValues, setTplValues] = useState({});

  // Phase 2 — Edit panel state
  const [editFile, setEditFile] = useState(null);
  const [editPreview, setEditPreview] = useState("");
  const [editPreset, setEditPreset] = useState("");
  const [editStyle, setEditStyle] = useState("");
  const [editPrompt, setEditPrompt] = useState("");
  const [editing, setEditing] = useState(false);

  const [library, setLibrary] = useState({ items: [], total: 0, limit: 24, offset: 0 });
  const [libLoading, setLibLoading] = useState(false);
  const [query, setQuery] = useState("");

  // Phase 3 — Brand kit integration
  const [brandKit, setBrandKit] = useState(null);
  const [applyBrand, setApplyBrand] = useState(false);
  const [applyWatermark, setApplyWatermark] = useState(false);
  const [watermarkPosition, setWatermarkPosition] = useState("bottom-right");

  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const r = await api.get("/creative-studio/status");
      setStatus(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Subscription-gated → still render a friendly upgrade prompt
      setStatus({ error: detail || "Could not load Creative Studio" });
    } finally { setStatusLoading(false); }
  }, []);

  const loadLibrary = useCallback(async () => {
    setLibLoading(true);
    try {
      const r = await api.get("/creative-studio/library", { params: { limit: 24, q: query || undefined } });
      setLibrary(r.data);
    } catch (e) {
      // Subscription gate may 403 here too — silent.
      setLibrary({ items: [], total: 0, limit: 24, offset: 0 });
    } finally { setLibLoading(false); }
  }, [query]);

  useEffect(() => { loadStatus(); loadLibrary(); }, [loadStatus, loadLibrary]);

  // Phase 3 — fetch brand kit once on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/creative-studio/brand-kit");
        setBrandKit(r.data);
      } catch { /* silent — page may be subscription-locked */ }
    })();
  }, []);

  const kitHasAnything = !!brandKit && (
    brandKit.primary_color || brandKit.secondary_color || brandKit.accent_color ||
    brandKit.slogan || brandKit.website || brandKit.logo_configured || brandKit.watermark_configured
  );

  const generate = async () => {
    if (prompt.trim().length < 4) {
      toast.error("Prompt must be at least 4 characters");
      return;
    }
    setGenerating(true);
    setLastImage(null);
    try {
      const r = await api.post("/creative-studio/generate", {
        prompt, aspect_ratio: aspect, title,
        apply_brand: applyBrand && kitHasAnything,
        apply_watermark: applyWatermark && brandKit?.watermark_configured,
        watermark_position: watermarkPosition,
      });
      setLastImage(r.data.image);
      toast.success("Image generated");
      loadLibrary();
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally { setGenerating(false); }
  };

  const removeImage = async (image_id) => {
    if (!await appConfirm("Delete this image?")) return;
    try {
      await api.delete(`/creative-studio/image/${image_id}`);
      toast.success("Deleted");
      loadLibrary(); loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // ── Phase 1.5: template builder ──
  const openTemplate = (tpl) => {
    setPickingTemplate(tpl);
    setTplValues({});
  };
  const applyTemplate = () => {
    if (!pickingTemplate) return;
    const required = pickingTemplate.fields.filter((f) => f.required);
    for (const f of required) {
      if (!(tplValues[f.key] || "").trim()) {
        toast.error(`${f.label} is required`);
        return;
      }
    }
    const composed = pickingTemplate.build(tplValues);
    setPrompt(composed);
    setAspect(pickingTemplate.aspect || "square");
    setPickingTemplate(null);
    toast.success(`Template "${pickingTemplate.label}" applied — review and Generate`);
  };

  // ── Phase 2: edit upload + submit ──
  const onEditFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    if (f.size > 12 * 1024 * 1024) {
      toast.error("Image must be 12 MB or smaller");
      return;
    }
    setEditFile(f);
    const url = URL.createObjectURL(f);
    setEditPreview(url);
  };

  const clearEditFile = () => {
    if (editPreview) URL.revokeObjectURL(editPreview);
    setEditFile(null); setEditPreview("");
  };

  const submitEdit = async () => {
    if (!editFile) { toast.error("Upload an image first"); return; }
    if (!editStyle && !editPreset && editPrompt.trim().length < 4) {
      toast.error("Choose a style/edit preset, or describe the edit (4+ chars)");
      return;
    }
    setEditing(true);
    setLastImage(null);
    try {
      const fd = new FormData();
      fd.append("file", editFile);
      if (editPrompt) fd.append("prompt", editPrompt);
      if (editStyle)  fd.append("style", editStyle);
      if (editPreset) fd.append("edit_preset", editPreset);
      if (title)      fd.append("title", title);
      fd.append("apply_brand", String(applyBrand && kitHasAnything));
      fd.append("apply_watermark", String(applyWatermark && brandKit?.watermark_configured));
      fd.append("watermark_position", watermarkPosition);
      const r = await api.post("/creative-studio/edit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setLastImage(r.data.image);
      toast.success("Edit complete");
      loadLibrary(); loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Edit failed");
    } finally { setEditing(false); }
  };

  // Subscription / connection gates
  if (statusLoading) {
    return <div className="p-10 text-center font-mono text-xs text-ink-muted">Loading Creative Studio…</div>;
  }

  if (status?.error) {
    return (
      <div className="p-6 md:p-10 max-w-3xl mx-auto" data-testid="creative-studio-locked">
        <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-brand-amber mb-4">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
        </Link>
        <div className="rounded-xl border border-brand-amber/30 bg-brand-amber/5 p-8 text-center">
          <Sparkles className="h-10 w-10 text-brand-amber mx-auto mb-3" />
          <h2 className="font-display text-xl font-bold mb-2">Creative Studio is on Pro Radio + Network</h2>
          <p className="text-sm text-ink-muted mb-4">{typeof status.error === "string" ? status.error : "Upgrade your plan to unlock Google Nano Banana 2."}</p>
          <Link to="/dashboard/billing" className="inline-block rounded-md bg-brand-amber text-bg font-bold px-4 py-2 text-sm">View plans →</Link>
        </div>
      </div>
    );
  }

  const connected = !!status?.connected;

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="creative-studio-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-brand-amber transition mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
      </Link>

      <PageHeader
        eyebrow="// CREATIVE STUDIO"
        title="Generate broadcast-grade visuals with Nano Banana 2."
        description="Logos, thumbnails, posters, podcast art, sponsor graphics — directly inside BroadcastFlow. Powered by Google Nano Banana 2 using your own API key."
      />

      {/* Connection banner */}
      {!connected && (
        <div className="mb-6 flex items-start gap-3 rounded-xl border border-brand-onair/40 bg-brand-onair/5 p-4" data-testid="not-connected-banner">
          <Plug className="h-5 w-5 text-brand-onair mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm">Connect Google Nano Banana 2</div>
            <div className="text-xs text-ink-muted mt-1">
              You need to connect a Google AI Studio API key in the AI Provider Center before generating images.
              {status?.openai_fallback_available && (
                <>
                  {" "}
                  <span className="text-cyan-300">
                    Or skip the BYOK and use the built-in {status?.openai_fallback_label || "OpenAI"} fallback ↓
                  </span>
                </>
              )}
            </div>
          </div>
          <Link to="/dashboard/providers" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-brand-amber/30 bg-brand-amber/10 text-brand-amber font-bold shrink-0">
            Connect Key <ExternalLink className="h-3 w-3" />
          </Link>
        </div>
      )}

      {/* Fallback availability hint (when connected) */}
      {connected && status?.openai_fallback_available && (
        <div
          className="mb-4 flex items-center gap-2 rounded-md border border-cyan-500/20 bg-cyan-500/5 px-3 py-2 text-[11px] text-cyan-200"
          data-testid="openai-fallback-hint"
        >
          <Sparkles className="h-3 w-3 text-cyan-300 shrink-0" />
          <span>
            <strong>Auto-fallback enabled:</strong> if {status?.provider_label || "Nano Banana"} hits a rate limit or error, we&apos;ll quietly retry on {status?.openai_fallback_label || "OpenAI Images"} so your generation still succeeds.
          </span>
        </div>
      )}

      {/* Stats row */}
      <div className="mb-6 grid grid-cols-3 gap-3" data-testid="stats-row">
        <div className="rounded-lg border border-line bg-bg-surface p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Connection</div>
          <div className="font-display text-base font-bold mt-1">
            {connected ? <span className="text-emerald-300">● Live</span> : <span className="text-brand-onair">● Off</span>}
          </div>
        </div>
        <div className="rounded-lg border border-line bg-bg-surface p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Today</div>
          <div className="font-display text-base font-bold mt-1" data-testid="stat-today">{status?.images_today ?? 0}</div>
        </div>
        <div className="rounded-lg border border-line bg-bg-surface p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Library</div>
          <div className="font-display text-base font-bold mt-1" data-testid="stat-total">{status?.images_total ?? 0}</div>
        </div>
      </div>

      {/* Tab switcher */}
      <div className="mb-4 inline-flex rounded-md border border-line bg-bg-surface p-1" data-testid="studio-tabs">
        <button
          onClick={() => setTab("generate")}
          data-testid="tab-generate"
          className={`text-xs px-3 py-1.5 rounded transition ${
            tab === "generate" ? "bg-brand-amber text-bg font-bold" : "text-ink-muted hover:text-ink-primary"
          }`}
        >
          <Wand2 className="h-3.5 w-3.5 inline mr-1" /> Generate
        </button>
        <button
          onClick={() => setTab("edit")}
          data-testid="tab-edit"
          className={`text-xs px-3 py-1.5 rounded transition ${
            tab === "edit" ? "bg-brand-amber text-bg font-bold" : "text-ink-muted hover:text-ink-primary"
          }`}
        >
          <Palette className="h-3.5 w-3.5 inline mr-1" /> Edit & Restyle
        </button>
      </div>

      {/* Phase 3 — Brand kit bar */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-bg-surface p-3" data-testid="brand-bar">
        <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted flex items-center gap-1">
          <Stamp className="h-3.5 w-3.5" /> Brand:
        </div>
        {kitHasAnything ? (
          <>
            <button
              onClick={() => setApplyBrand(!applyBrand)}
              data-testid="toggle-apply-brand"
              className={`text-xs px-2.5 py-1 rounded border transition ${
                applyBrand ? "border-brand-amber bg-brand-amber/10 text-brand-amber font-bold" : "border-line bg-bg-elevated text-ink-muted hover:border-brand-amber/30"
              }`}
              title="Bias the prompt with your brand colors, slogan, website."
            >
              {applyBrand ? "✓ Brand-aware prompt" : "Apply brand kit"}
            </button>
            {brandKit?.watermark_configured && (
              <button
                onClick={() => setApplyWatermark(!applyWatermark)}
                data-testid="toggle-apply-watermark"
                className={`text-xs px-2.5 py-1 rounded border transition ${
                  applyWatermark ? "border-brand-blue bg-brand-blue/10 text-brand-blue font-bold" : "border-line bg-bg-elevated text-ink-muted hover:border-brand-blue/30"
                }`}
                title="Composite your watermark onto the output (pixel-perfect)."
              >
                {applyWatermark ? "✓ Watermark" : "Add watermark"}
              </button>
            )}
            {applyWatermark && (
              <select
                value={watermarkPosition}
                onChange={(e) => setWatermarkPosition(e.target.value)}
                data-testid="watermark-position"
                className="text-xs px-2 py-1 rounded border border-line bg-bg-elevated text-ink-primary"
              >
                <option value="bottom-right">Bottom-right</option>
                <option value="bottom-left">Bottom-left</option>
                <option value="top-right">Top-right</option>
                <option value="top-left">Top-left</option>
                <option value="center">Center</option>
              </select>
            )}
            <Link to="/dashboard/creative-studio/brand-kit" data-testid="open-brand-kit"
              className="ml-auto text-[11px] text-ink-muted hover:text-brand-amber inline-flex items-center gap-1">
              Edit kit <ExternalLink className="h-3 w-3" />
            </Link>
          </>
        ) : (
          <>
            <span className="text-xs text-ink-muted">Set up your station&apos;s colors, slogan, logo and watermark.</span>
            <Link to="/dashboard/creative-studio/brand-kit" data-testid="setup-brand-kit"
              className="ml-auto inline-flex items-center gap-1 text-xs px-3 py-1 rounded-md border border-brand-amber/30 bg-brand-amber/10 text-brand-amber font-bold">
              Set up brand kit →
            </Link>
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-6 mb-10">
        {tab === "generate" ? (
        /* Generator */
        <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="generator-card">
          <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-brand-amber" /> Generate
          </h3>

          {/* Phase 1.5 — Template picker */}
          <div className="mb-4" data-testid="template-picker">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted flex items-center gap-1">
                <LayoutTemplate className="h-3 w-3" /> Quick templates
              </label>
              <span className="text-[10px] font-mono text-ink-muted">{TEMPLATES.length} broadcast presets</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {TEMPLATES.slice(0, 6).map((tpl) => (
                <button
                  key={tpl.id}
                  onClick={() => openTemplate(tpl)}
                  data-testid={`template-${tpl.id}`}
                  className="group flex items-center gap-2 text-xs px-2 py-2 rounded-md border border-line bg-bg-elevated text-left hover:border-brand-amber/50 hover:bg-brand-amber/5 transition"
                >
                  <tpl.icon className="h-4 w-4 text-brand-amber shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-ink-primary truncate">{tpl.label}</div>
                    <div className="text-[10px] text-ink-muted truncate">{tpl.description}</div>
                  </div>
                </button>
              ))}
            </div>
            {TEMPLATES.length > 6 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {TEMPLATES.slice(6).map((tpl) => (
                  <button
                    key={tpl.id}
                    onClick={() => openTemplate(tpl)}
                    data-testid={`template-${tpl.id}`}
                    className="text-[10px] font-mono px-2 py-1 rounded border border-line bg-bg-elevated text-ink-muted hover:border-brand-amber/40 hover:text-brand-amber transition inline-flex items-center gap-1"
                  >
                    <tpl.icon className="h-3 w-3" /> {tpl.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Prompt</label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe what you want… e.g. 'Promotional poster for a synthwave concert called NEON DRIVE, glowing pink and cyan, retro 80s grid.'"
            rows={5}
            data-testid="prompt-input"
            className="mt-1 w-full rounded-md border border-line bg-bg-elevated text-ink-primary px-3 py-2 text-sm placeholder:text-ink-muted focus:border-brand-amber focus:outline-none resize-y"
          />
          <div className="mt-2 flex flex-wrap gap-1">
            {PROMPT_IDEAS.map((p, i) => (
              <button
                key={p}
                onClick={() => setPrompt(p)}
                data-testid={`idea-chip-${i}`}
                className="text-[10px] font-mono px-2 py-1 rounded border border-line bg-bg-elevated text-ink-muted hover:border-brand-amber/40 hover:text-brand-amber transition"
              >
                {p.slice(0, 38)}…
              </button>
            ))}
          </div>

          <div className="mt-4 grid grid-cols-4 gap-2" data-testid="aspect-row">
            {ASPECTS.map((a) => (
              <button
                key={a.key}
                onClick={() => setAspect(a.key)}
                data-testid={`aspect-${a.key}`}
                className={`text-xs px-2 py-2 rounded-md border transition ${
                  aspect === a.key
                    ? "border-brand-amber bg-brand-amber/10 text-brand-amber"
                    : "border-line bg-bg-elevated text-ink-muted hover:border-brand-amber/30"
                }`}
              >
                <div className="font-medium">{a.label}</div>
                <div className="font-mono text-[9px] mt-0.5">{a.dim}</div>
              </button>
            ))}
          </div>

          <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mt-4 block">Optional title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. NEON_DRIVE_poster_v1"
            data-testid="title-input"
            className="mt-1 w-full rounded-md border border-line bg-bg-elevated text-ink-primary px-3 py-2 text-sm placeholder:text-ink-muted focus:border-brand-amber focus:outline-none"
          />

          <button
            onClick={generate}
            disabled={generating || !connected || prompt.trim().length < 4}
            data-testid="generate-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-brand-amber text-bg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
            {generating ? "Generating…" : "Generate image"}
          </button>
          {!connected && (
            <div className="mt-2 text-[11px] text-ink-muted text-center">Connect a Google API key first.</div>
          )}
        </div>
        ) : (
        /* Phase 2 — Edit & Restyle panel */
        <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="edit-card">
          <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
            <Palette className="h-5 w-5 text-brand-amber" /> Edit & Restyle
          </h3>

          {/* Upload zone */}
          <label
            htmlFor="cs-edit-upload"
            data-testid="edit-dropzone"
            className="block rounded-md border-2 border-dashed border-line bg-bg-elevated p-4 text-center cursor-pointer hover:border-brand-amber/50 transition"
          >
            {editPreview ? (
              <div className="relative">
                <img src={editPreview} alt="Source" className="max-h-48 mx-auto rounded" data-testid="edit-preview-thumb" />
                <button
                  onClick={(e) => { e.preventDefault(); clearEditFile(); }}
                  className="absolute top-1 right-1 bg-bg/80 rounded-full p-1 text-ink-muted hover:text-brand-onair"
                  data-testid="edit-clear-file"
                >
                  <X className="h-3 w-3" />
                </button>
                <div className="mt-2 font-mono text-[10px] text-ink-muted truncate">{editFile?.name}</div>
              </div>
            ) : (
              <div className="py-3">
                <Upload className="h-7 w-7 text-ink-muted mx-auto mb-1" />
                <div className="text-sm">Upload an image to edit</div>
                <div className="text-[11px] text-ink-muted">PNG / JPG / WEBP · up to 12MB</div>
              </div>
            )}
            <input
              id="cs-edit-upload"
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onEditFileChange}
              data-testid="edit-file-input"
            />
          </label>

          {/* Edit presets */}
          <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mt-4 block">Edit operation</label>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="edit-presets-row">
            {EDIT_PRESETS.map((ep) => (
              <button
                key={ep.id}
                onClick={() => setEditPreset(editPreset === ep.id ? "" : ep.id)}
                data-testid={`edit-preset-${ep.id}`}
                title={ep.hint}
                className={`text-[11px] px-2 py-1 rounded border transition ${
                  editPreset === ep.id
                    ? "border-brand-amber bg-brand-amber/10 text-brand-amber font-bold"
                    : "border-line bg-bg-elevated text-ink-muted hover:border-brand-amber/30"
                }`}
              >
                {ep.label}
              </button>
            ))}
          </div>

          {/* Style presets */}
          <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mt-4 block">Style conversion</label>
          <div className="mt-1 flex flex-wrap gap-1.5" data-testid="style-presets-row">
            {STYLE_PRESETS.map((sp) => (
              <button
                key={sp.id}
                onClick={() => setEditStyle(editStyle === sp.id ? "" : sp.id)}
                data-testid={`style-preset-${sp.id}`}
                title={sp.hint}
                className={`text-[11px] px-2 py-1 rounded border transition ${
                  editStyle === sp.id
                    ? "border-brand-blue bg-brand-blue/10 text-brand-blue font-bold"
                    : "border-line bg-bg-elevated text-ink-muted hover:border-brand-blue/30"
                }`}
              >
                {sp.label}
              </button>
            ))}
          </div>

          {/* Free-text instruction */}
          <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mt-4 block">Additional instructions (optional)</label>
          <textarea
            value={editPrompt}
            onChange={(e) => setEditPrompt(e.target.value)}
            placeholder="e.g. 'Make the background a sunset cityscape' or 'Add the headline NEW MUSIC FRIDAY across the top'"
            rows={3}
            data-testid="edit-prompt-input"
            className="mt-1 w-full rounded-md border border-line bg-bg-elevated text-ink-primary px-3 py-2 text-sm placeholder:text-ink-muted focus:border-brand-amber focus:outline-none resize-y"
          />

          <button
            onClick={submitEdit}
            disabled={editing || !connected || !editFile}
            data-testid="edit-submit-btn"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-brand-amber text-bg font-bold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {editing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Palette className="h-4 w-4" />}
            {editing ? "Editing…" : "Apply edit"}
          </button>
          {!connected && (
            <div className="mt-2 text-[11px] text-ink-muted text-center">Connect a Google API key first.</div>
          )}
        </div>
        )}

        {/* Preview */}
        <div className="rounded-xl border border-line bg-bg-surface p-5" data-testid="preview-card">
          <h3 className="font-display text-lg font-bold mb-3 flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-brand-blue" /> Preview
          </h3>
          {(generating || editing) ? (
            <div className="aspect-square rounded-md border border-dashed border-line bg-bg-elevated grid place-items-center" data-testid="preview-loading">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-brand-amber mx-auto mb-2" />
                <div className="font-mono text-xs text-ink-muted">{editing ? "Nano Banana 2 is editing…" : "Nano Banana 2 is composing…"}</div>
              </div>
            </div>
          ) : lastImage ? (
            <div data-testid="preview-image">
              <img
                src={authedImageUrl(`/api/creative-studio/image/${lastImage.image_id}`)}
                alt={lastImage.title || "Generated image"}
                className="rounded-md w-full border border-line"
              />
              <div className="mt-3 flex items-center justify-between gap-2">
                <div className="text-xs text-ink-muted truncate flex-1" title={lastImage.prompt}>
                  {lastImage.title || (lastImage.prompt || "").slice(0, 60) + "…"}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {lastImage.trial_watermarked && (
                    <WatermarkUnlockPrompt
                      kind="image"
                      label={lastImage.title || (lastImage.prompt || "").slice(0, 40)}
                      previewSrc={authedImageUrl(`/api/creative-studio/image/${lastImage.image_id}`)}
                    />
                  )}
                  <a
                    href={authedImageUrl(`/api/creative-studio/image/${lastImage.image_id}`)}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-brand-amber/30 bg-brand-amber/10 text-brand-amber font-bold"
                    data-testid="download-last"
                  >
                    <Download className="h-3 w-3" /> Download
                  </a>
                  <button
                    onClick={() => sendToKaraoke(lastImage)}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-pink-500/40 bg-pink-500/10 text-pink-300 font-bold"
                    data-testid="use-in-karaoke-last"
                    title="Use as karaoke background in Beds & FX"
                  >
                    <Film className="h-3 w-3" /> Karaoke BG
                  </button>
                  <button
                    onClick={() => setPublishImage(lastImage)}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-blue-500/40 bg-blue-500/10 text-blue-300 font-bold"
                    data-testid="publish-blog-last"
                    title="Publish to WordPress with this image as featured media"
                  >
                    <Send className="h-3 w-3" /> Publish to blog
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="aspect-square rounded-md border border-dashed border-line bg-bg-elevated grid place-items-center" data-testid="preview-empty">
              <div className="text-center px-6">
                <ImageIcon className="h-10 w-10 text-ink-muted mx-auto mb-2" />
                <div className="text-sm text-ink-muted">Your generated image will appear here.</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Library */}
      <div data-testid="library-section">
        <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-3">
          <div>
            <h3 className="font-display text-lg font-bold">Image library</h3>
            <p className="text-xs text-ink-muted">Everything you generate, saved per station.</p>
          </div>
          <div className="sm:ml-auto relative">
            <Search className="h-4 w-4 text-ink-muted absolute left-2 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadLibrary()}
              placeholder="Search prompts, titles, tags…"
              data-testid="library-search"
              className="pl-7 pr-2 py-2 w-64 rounded-md border border-line bg-bg-elevated text-ink-primary text-xs focus:border-brand-amber focus:outline-none"
            />
          </div>
        </div>

        {libLoading ? (
          <div className="py-10 text-center font-mono text-xs text-ink-muted">Loading library…</div>
        ) : library.items.length === 0 ? (
          <div className="rounded-xl border border-dashed border-line bg-bg-surface p-10 text-center" data-testid="library-empty">
            <ImageIcon className="h-10 w-10 text-ink-muted mx-auto mb-2" />
            <div className="text-sm text-ink-muted">No images yet — generate your first one above.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="library-grid">
            {library.items.map((img) => (
              <div
                key={img.image_id}
                className="group relative rounded-md overflow-hidden border border-line bg-bg-surface aspect-square"
                data-testid={`library-tile-${img.image_id}`}
              >
                <img
                  src={authedImageUrl(`/api/creative-studio/image/${img.image_id}`)}
                  alt={img.title || img.prompt?.slice(0, 60) || "Generated"}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-bg/90 to-transparent opacity-0 group-hover:opacity-100 transition">
                  <div className="text-[10px] text-ink-primary truncate" title={img.prompt}>
                    {img.title || (img.prompt || "").slice(0, 40)}
                  </div>
                  <div className="mt-1 flex justify-between flex-wrap gap-1">
                    <a
                      href={authedImageUrl(`/api/creative-studio/image/${img.image_id}`)}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[10px] text-brand-amber inline-flex items-center gap-0.5"
                      data-testid={`download-${img.image_id}`}
                    >
                      <Download className="h-3 w-3" /> DL
                    </a>
                    <button
                      onClick={() => sendToKaraoke(img)}
                      className="text-[10px] text-pink-300 inline-flex items-center gap-0.5"
                      data-testid={`use-in-karaoke-${img.image_id}`}
                      title="Use as karaoke background"
                    >
                      <Film className="h-3 w-3" /> KK
                    </button>
                    <button
                      onClick={() => setPublishImage(img)}
                      className="text-[10px] text-blue-300 inline-flex items-center gap-0.5"
                      data-testid={`publish-blog-${img.image_id}`}
                      title="Publish to WordPress"
                    >
                      <Send className="h-3 w-3" /> WP
                    </button>
                    <button
                      onClick={() => removeImage(img.image_id)}
                      className="text-[10px] text-brand-onair inline-flex items-center gap-0.5"
                      data-testid={`delete-${img.image_id}`}
                    >
                      <Trash2 className="h-3 w-3" /> Del
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 text-[11px] font-mono text-ink-muted">{library.total} image(s) total</div>
      </div>

      {/* Phase 1.5 — Template builder modal */}
      {pickingTemplate && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-bg/80 backdrop-blur p-4"
          onClick={() => setPickingTemplate(null)}
          data-testid="template-modal"
        >
          <div
            className="w-full max-w-md rounded-xl border border-line bg-bg-surface p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="rounded-md p-2 bg-brand-amber/10 text-brand-amber">
                <pickingTemplate.icon className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg font-bold">{pickingTemplate.label}</h3>
                <p className="text-xs text-ink-muted mt-0.5">{pickingTemplate.description}</p>
              </div>
              <button
                onClick={() => setPickingTemplate(null)}
                className="text-ink-muted hover:text-ink-primary"
                data-testid="template-modal-close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {pickingTemplate.fields.length === 0 ? (
              <div className="text-sm text-ink-muted mb-4">No inputs needed — this template generates a standard preset.</div>
            ) : (
              <div className="space-y-3 mb-4">
                {pickingTemplate.fields.map((f) => (
                  <div key={f.key}>
                    <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">
                      {f.label} {f.required && <span className="text-brand-onair">*</span>}
                    </label>
                    <input
                      type="text"
                      value={tplValues[f.key] || ""}
                      onChange={(e) => setTplValues((p) => ({ ...p, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      data-testid={`tpl-field-${f.key}`}
                      className="mt-1 w-full rounded-md border border-line bg-bg-elevated text-ink-primary px-3 py-2 text-sm placeholder:text-ink-muted focus:border-brand-amber focus:outline-none"
                    />
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={applyTemplate}
              data-testid="tpl-apply-btn"
              className="w-full px-4 py-2.5 rounded-md bg-brand-amber text-bg font-bold inline-flex items-center justify-center gap-2"
            >
              <Sparkles className="h-4 w-4" /> Apply template
            </button>
            <div className="mt-2 text-[10px] text-ink-muted text-center">Aspect ratio will be set to <strong>{pickingTemplate.aspect}</strong>.</div>
          </div>
        </div>
      )}

      {/* Update #131 — Publish this AI image straight to WordPress */}
      <CreativeStudioBlogPublishModal
        open={!!publishImage}
        image={publishImage}
        onClose={() => setPublishImage(null)}
      />
    </div>
  );
}
